import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useManageAdditionalDriverDetails } from '@acorn-portal/shared/src/hooks/use-manage-additional-driver-details';
import useRateRenewal from '@acorn-portal/shared/src/hooks/useRateRenewal';
import { DriverDetails } from '@acorn-portal/shared/src/services/renewalsApi';
import { updateChangeOfDriver } from '@acorn-portal/shared/src/store/renewalChanges.slice';
import { omitFromArray } from '@acorn-portal/shared/src/utils/omit-from-array';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import ManageDrivers from '../../components/manage-drivers';
import { IManageDriversProps } from '../../components/manage-drivers/types';
import useRenewalNavigation from '../../hooks/useRenewalNavigation';
import { ROUTE_PATHS } from '../../utils/Routes';

const RenewalManageDrivers = () => {
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const renewalState = useAppSelector((redux) => redux.renewal);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const { goToRenewalHome } = useRenewalNavigation();
	const vehicleUse = renewalState.vehicleDetails.vehicleUse;
	const dlnText =
		'Please make sure you have the additional drivers consent & driving licence number with you, as we may need this information to update your renewal quote.';

	const { rateRenewal, loadingRenewalQuote } = useRateRenewal();
	const {
		additionalDrivers,
		setAdditionalDrivers,
		getAdditionalDriversRenewal,
		onRemoveClick,
		onEditClick,
		showForm,
		onSetMainDriver,
		toggleForm,
		setItem,
		setSelectedItem,
		onFullFormSubmit,
		onShortFormSubmit,
		isDriverModalOpen,
		toggleDriverModal,
		onDelete,
		selectedItem,
	} = useManageAdditionalDriverDetails({
		renewalState,
	});

	async function submitRenewalChanges() {
		if (!additionalDrivers) return;
		// Remove properties that are not needed for the API call (same as MTA)
		const updatedAdditionalDrivers = additionalDrivers.map((driver) =>
			omitFromArray(driver, ['otherEmployment', 'isNewDriver']),
		);

		setLoading(true);

		try {
			const renewalUpdated = {
				...renewalState,
				// TODO: remove the type workaround when the backend maps hasNonMotorConvictions to hasCriminalConvictions for renewals
				drivers: updatedAdditionalDrivers as DriverDetails[],
			};
			dispatch(updateChangeOfDriver(true));

			await rateRenewal({ renewalState: renewalUpdated, fullPolicy });
		} catch (error) {
			console.error(error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	}

	const onSubmitRenewal = async () => {
		submitRenewalChanges();
	};

	useEffect(() => {
		if (additionalDrivers === undefined || additionalDrivers.length === 0) {
			getAdditionalDriversRenewal();
		}
		if (!renewalState.policyReference) {
			navigate(ROUTE_PATHS.home);
		}
	}, [additionalDrivers, renewalState, getAdditionalDriversRenewal]);

	const props: IManageDriversProps = {
		loading: loadingRenewalQuote || loading,
		additionalDrivers,
		setAdditionalDrivers,
		vehicleUse,
		showForm,
		openDriverModal: isDriverModalOpen,
		selectedItem,
		dlnText,
		onRemoveClick,
		onEditClick,
		onSetMainDriver,
		toggleForm,
		setItem,
		setSelectedItem,
		onShortFormSubmit,
		onFullFormSubmit,
		toggleDriverModal,
		onDelete,
		onPageSubmit: onSubmitRenewal,
		onDiscard: goToRenewalHome,
	};

	return <ManageDrivers {...props} />;
};

export default RenewalManageDrivers;
